import { useState, useEffect, useRef } from 'react'
import { api } from '../services/api'
import {
  ComputerDesktopIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  CircleStackIcon,
  RocketLaunchIcon,
  EnvelopeIcon,
  LockClosedIcon,
  CpuChipIcon,
  GlobeAltIcon,
  ChartBarIcon,
  FireIcon,
  ServerIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline'

const SystemMonitor = () => {
  const [systemStatus, setSystemStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef(null)

  const getStatusColor = (status, health) => {
    if (status === 'healthy' || health === 'healthy') return 'text-green-600 bg-green-50 border-green-200'
    if (status === 'degraded' || health === 'degraded') return 'text-amber-600 bg-amber-50 border-amber-200'
    if (status === 'unhealthy' || health === 'unhealthy') return 'text-red-600 bg-red-50 border-red-200'
    return 'text-slate-500 bg-slate-50 border-slate-200'
  }

  const StatusIcon = ({ status, health, className = 'h-5 w-5' }) => {
    if (status === 'healthy' || health === 'healthy') return <CheckCircleIcon className={`${className} text-green-600`} />
    if (status === 'degraded' || health === 'degraded') return <ExclamationTriangleIcon className={`${className} text-amber-600`} />
    if (status === 'unhealthy' || health === 'unhealthy') return <XCircleIcon className={`${className} text-red-600`} />
    return <QuestionMarkCircleIcon className={`${className} text-slate-400`} />
  }

  const fetchSystemStatus = async () => {
    try {
      const response = await api.get('/admin/system/status')
      setSystemStatus(response.data)
      setError(null)
      setLastUpdate(new Date())
    } catch (err) {
      console.error('Failed to fetch system status:', err)
      setError('Failed to fetch system status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSystemStatus()

    if (autoRefresh) {
      intervalRef.current = setInterval(fetchSystemStatus, 10000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [autoRefresh])

  const handleRefresh = () => {
    setLoading(true)
    fetchSystemStatus()
  }

  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => {
      if (!prev) {
        intervalRef.current = setInterval(fetchSystemStatus, 10000)
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
      return !prev
    })
  }

  if (loading && !systemStatus) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">System Monitor</h1>
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-600 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Loading system status...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !systemStatus) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">System Monitor</h1>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">System Status Unavailable</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const componentIcons = {
    database: CircleStackIcon,
    api: RocketLaunchIcon,
    email: EnvelopeIcon,
    auth: LockClosedIcon,
    rag: CpuChipIcon,
    scraper: GlobeAltIcon,
  }

  const componentLabels = {
    database: 'Database',
    api: 'API Server',
    email: 'Email Service',
    auth: 'Authentication',
    rag: 'RAG/AI Engine',
    scraper: 'Web Scraper',
  }

  const componentFields = {
    database: [
      { key: 'status', label: 'Status' },
      { key: 'response_time', label: 'Response Time' },
      { key: 'connections', label: 'Connection Pool' },
    ],
    api: [
      { key: 'status', label: 'Status' },
      { key: 'active_requests', label: 'Active Requests', fallback: '0' },
      { key: 'total_requests', label: 'Total Requests', fallback: '0' },
    ],
    email: [
      { key: 'status', label: 'Status' },
      { key: 'queue_size', label: 'Queue Size', fallback: '0' },
      { key: 'sent_today', label: 'Sent Today', fallback: '0' },
    ],
    auth: [
      { key: 'status', label: 'Status' },
      { key: 'active_sessions', label: 'Active Sessions', fallback: '0' },
      { key: 'failed_logins', label: 'Failed Logins', fallback: '0' },
    ],
    rag: [
      { key: 'status', label: 'Status' },
      { key: 'active_models', label: 'Active Models', fallback: '0' },
      { key: 'queries_today', label: 'Queries Today', fallback: '0' },
    ],
    scraper: [
      { key: 'status', label: 'Status' },
      { key: 'active_jobs', label: 'Active Jobs', fallback: '0' },
      { key: 'success_rate', label: 'Success Rate' },
    ],
  }

  const metricItems = [
    { key: 'cpu_usage', label: 'CPU Usage', icon: FireIcon },
    { key: 'memory_usage', label: 'Memory Usage', icon: ServerIcon },
    { key: 'active_users', label: 'Active Users', icon: UsersIcon, fallback: '0' },
    { key: 'requests_per_minute', label: 'Requests/min', icon: ArrowTrendingUpIcon, fallback: '0' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">System Monitor</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time system health and performance monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleAutoRefresh}
            className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
              autoRefresh
                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            {autoRefresh ? <PlayIcon className="h-4 w-4" /> : <PauseIcon className="h-4 w-4" />}
            <span>{autoRefresh ? 'Auto ON' : 'Auto OFF'}</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {lastUpdate && (
            <span className="text-xs text-slate-400">
              {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {systemStatus && (
        <>
          {/* Overall System Status */}
          <div className={`rounded-xl border-2 p-5 ${getStatusColor(systemStatus.overall_status)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusIcon status={systemStatus.overall_status} className="h-8 w-8" />
                <div>
                  <h2 className="text-lg font-bold">Overall System Status</h2>
                  <p className="text-sm capitalize font-medium">{systemStatus.overall_status}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider opacity-75">System Uptime</div>
                <div className="text-sm font-mono font-medium">{systemStatus.uptime || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Component Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(componentLabels).map(([key, label]) => {
              const comp = systemStatus.components?.[key]
              if (!comp) return null
              const Icon = componentIcons[key]
              const fields = componentFields[key]
              return (
                <div key={key} className={`rounded-xl border p-5 ${getStatusColor(comp.status)}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      <h3 className="text-sm font-semibold">{label}</h3>
                    </div>
                    <StatusIcon status={comp.status} className="h-5 w-5" />
                  </div>
                  <div className="space-y-2">
                    {fields.map((field) => (
                      <div key={field.key} className="flex justify-between">
                        <span className="text-xs text-slate-500">{field.label}</span>
                        <span className="text-xs font-mono font-medium capitalize">
                          {field.key === 'status' ? comp.status : (comp[field.key] || field.fallback || 'N/A')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {/* System Metrics */}
          {systemStatus.metrics && (
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-slate-400" />
                System Metrics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <div key={item.key} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{item.label}</p>
                          <p className="text-2xl font-bold text-slate-900 mt-1">
                            {systemStatus.metrics[item.key] || item.fallback || 'N/A'}
                          </p>
                        </div>
                        <Icon className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Recent Events */}
          {systemStatus.events && systemStatus.events.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-slate-400" />
                Recent System Events
              </h2>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {systemStatus.events.map((event, index) => (
                    <div key={index} className={`p-4 border-l-4 ${
                      event.level === 'error' ? 'border-red-500 bg-red-50' :
                      event.level === 'warning' ? 'border-amber-500 bg-amber-50' :
                      'border-brand-500 bg-brand-50'
                    } ${index < systemStatus.events.length - 1 ? 'border-b border-slate-100' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {event.level === 'error' ? (
                            <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                          ) : event.level === 'warning' ? (
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                          ) : (
                            <InformationCircleIcon className="h-5 w-5 text-brand-600 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-900">{event.message}</p>
                            <p className="text-xs text-slate-500">{event.component}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">{event.timestamp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SystemMonitor
